import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ExplanationBox } from '../components/ExplanationBox';
import { api, type StartAttemptResponse } from '../lib/api';
import { ErrorView } from '../components/ErrorView';
import { Loader } from '../components/Loader';
import { Timer } from '../components/Timer';
import { haptic } from '../lib/telegram';
import { ZoomableImage } from '../components/ZoomableImage';

// Стан відповіді на одне питання
type Pick = {
  selectedId: number;
  isCorrect: boolean;
  correctAnswerId: number | null;
  explanation: string | null;
};

/**
 * Сторінка офіційного іспиту (OFFICIAL_20).
 *  - 20 питань, 20 хвилин (жорсткий таймер)
 *  - Після відповіді автоматично перекидаємо на наступне нерозв'язане питання
 *  - У навігації 1..20 номери підсвічуємо зеленим (правильно) / червоним (неправильно)
 *  - На картках варіантів показуємо правильну/неправильну розмітку коли вже відповіли
 *  - До завершення можна повертатись по навігації і перевибирати відповідь
 */
export function Exam() {
  const { attemptId } = useParams<{ attemptId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const initial = (location.state as StartAttemptResponse | null) ?? null;
  const [data] = useState<StartAttemptResponse | null>(initial);

  const [index, setIndex] = useState(0);
  const [picks, setPicks] = useState<Record<number, Pick>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const questionStartRef = useRef<number>(Date.now());

  useEffect(() => {
    questionStartRef.current = Date.now();
  }, [index]);

  if (!attemptId) return <ErrorView error={new Error('Невалідний ідентифікатор спроби')} />;
  if (!data) {
    return (
      <div className="p-6 text-center">
        <p className="mb-4 text-tg-hint">Іспит не запущено.</p>
        <button onClick={() => navigate('/', { replace: true })} className="btn-primary">
          На головну
        </button>
      </div>
    );
  }

  const total = data.questions.length;
  const current = data.questions[index]!;
  const currentPick = picks[current.id];
  const answeredCount = Object.keys(picks).length;

  async function pick(answerId: number) {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    const timeMs = Date.now() - questionStartRef.current;
    try {
      const res = await api.submitAnswer(attemptId!, {
        questionId: current.id,
        answerId,
        timeMs,
      });
      const newPicks = {
        ...picks,
        [current.id]: {
          selectedId: answerId,
          isCorrect: res.isCorrect,
          correctAnswerId: res.correctAnswerId,
          explanation: res.explanation,
        },
      };
      setPicks(newPicks);
      if (res.isCorrect) haptic.success();
      else haptic.error();

      // Як у СЦ МВС — допустимо максимум 2 помилки. Якщо набралось 3+ — одразу
      // завершуємо іспит: здати вже неможливо, тому не змушуємо продовжувати.
      const wrongCount = Object.values(newPicks).filter((p) => !p.isCorrect).length;
      if (wrongCount >= 3) {
        setTimeout(() => void finish(), 800);
        return;
      }

      // Авто-перехід на наступне нерозв'язане питання (з невеликою паузою — щоб юзер
      // встиг побачити підсвітку правильно/неправильно)
      const nextIdx = findNextUnanswered(index, data!.questions, newPicks);
      if (nextIdx !== null) {
        setTimeout(() => setIndex(nextIdx), 600);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Помилка відправки');
      haptic.error();
    } finally {
      setSubmitting(false);
    }
  }

  function goTo(i: number) {
    if (i < 0 || i >= total || i === index) return;
    haptic.light();
    setIndex(i);
  }

  function skip() {
    haptic.light();
    const nextIdx = findNextUnanswered(index, data!.questions, picks);
    if (nextIdx !== null) setIndex(nextIdx);
    else if (index < total - 1) setIndex(index + 1);
  }

  async function finish() {
    setSubmitting(true);
    try {
      await api.finishAttempt(attemptId!);
      haptic.success();
      navigate(`/result/${attemptId}`, { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Помилка завершення');
      setSubmitting(false);
    }
  }

  function tryFinish() {
    const left = total - answeredCount;
    if (left > 0) {
      const ok = confirm(
        `У вас ${left} не відповідано. Завершити? Невідповідані будуть зараховані як неправильні.`,
      );
      if (!ok) return;
    }
    void finish();
  }

  return (
    <>
      <header className="sticky top-0 z-10 border-b border-tg-secondary-bg bg-tg-bg/95 backdrop-blur">
        <div className="space-y-3 px-4 pt-[env(safe-area-inset-top)] pb-3">
          <div className="flex items-center justify-between pt-3">
            <button
              onClick={() => {
                if (confirm('Перервати іспит? Прогрес не буде збережено.'))
                  navigate('/', { replace: true });
              }}
              className="text-sm text-tg-hint"
              aria-label="Назад"
            >
              ‹
            </button>
            <div className="text-base font-medium">Іспит</div>
            <Timer totalSeconds={data.timeLimitSeconds} onExpire={finish} />
          </div>

          <div className="flex gap-1.5 overflow-x-auto pb-1 no-scrollbar">
            {data.questions.map((q, i) => {
              const p = picks[q.id];
              const isActive = i === index;
              let cls =
                'grid h-9 w-9 shrink-0 place-items-center rounded-full text-sm font-semibold transition';
              if (p) {
                // Зелений = правильна, червоний = неправильна
                cls += p.isCorrect
                  ? ' bg-tg-success text-white'
                  : ' bg-tg-destructive text-white';
              } else if (isActive) {
                cls += ' bg-tg-button text-tg-button-text';
              } else {
                cls += ' bg-tg-secondary-bg text-tg-hint';
              }
              if (isActive && p) cls += ' ring-2 ring-tg-button ring-offset-2 ring-offset-tg-bg';
              return (
                <button
                  key={q.id}
                  onClick={() => goTo(i)}
                  className={cls}
                  aria-label={`Питання ${i + 1}`}
                >
                  {i + 1}
                </button>
              );
            })}
          </div>
        </div>
      </header>

      <div className="space-y-2 p-3">
        {error && (
          <div className="card border border-tg-destructive text-sm text-tg-destructive">{error}</div>
        )}

        <article className="rounded-xl bg-tg-secondary-bg p-3.5">
          <p className="mb-2.5 text-base leading-snug">{current.textUk}</p>
          {current.imageUrl && <ZoomableImage src={current.imageUrl} />}
        </article>

        <div className="space-y-2">
          {current.answers.map((a, idx) => {
            const isPicked = currentPick?.selectedId === a.id;
            const isRightOne = currentPick?.correctAnswerId === a.id;
            let cls = 'flex w-full items-center gap-3 rounded-xl bg-tg-secondary-bg p-3 text-left text-[15px] leading-snug transition';
            if (currentPick) {
              if (isRightOne) cls += ' border border-tg-success bg-tg-success/10';
              else if (isPicked) cls += ' border border-tg-destructive bg-tg-destructive/10';
              else cls += ' opacity-60';
            } else {
              cls += ' hover:bg-tg-bg/40';
            }
            return (
              <button
                key={a.id}
                disabled={submitting || !!currentPick}
                onClick={() => pick(a.id)}
                className={cls}
              >
                <span
                  className={`grid h-7 w-7 shrink-0 place-items-center rounded-full text-xs font-semibold ${
                    isPicked && !isRightOne
                      ? 'bg-tg-destructive text-white'
                      : isRightOne
                        ? 'bg-tg-success text-white'
                        : 'bg-tg-bg'
                  }`}
                >
                  {String.fromCharCode(65 + idx)}
                </span>
                <span className="flex-1">{a.textUk}</span>
                {currentPick && isRightOne && <span className="shrink-0">✓</span>}
                {currentPick && isPicked && !isRightOne && <span className="shrink-0">✗</span>}
              </button>
            );
          })}
        </div>

        {/* Пояснення — за кнопкою, з оцінкою 👍/👎 */}
        {currentPick?.explanation && (
          <ExplanationBox
            key={current.id}
            questionId={current.id}
            explanation={currentPick.explanation}
          />
        )}

        <div className="flex gap-2 pt-2">
          <button onClick={skip} disabled={submitting} className="btn-secondary flex-1">
            Пропустити
          </button>
          <button onClick={tryFinish} disabled={submitting} className="btn-primary flex-1">
            Завершити
          </button>
        </div>

        <div className="text-center text-xs text-tg-hint">
          Відповіли: {answeredCount}/{total}
        </div>
        {submitting && <Loader />}
      </div>
    </>
  );
}

function findNextUnanswered(
  from: number,
  questions: { id: number }[],
  picks: Record<number, Pick>,
): number | null {
  for (let i = from + 1; i < questions.length; i++) {
    if (!picks[questions[i]!.id]) return i;
  }
  for (let i = 0; i < from; i++) {
    if (!picks[questions[i]!.id]) return i;
  }
  return null;
}
